/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { DEFAULT_BRAND, type Brand } from './brand';

/**
 * Per-brand COPY selection: which i18n key a surface reads, chosen by brand.
 *
 * WHY THIS FILE EXISTS: the copy-parity sweep landed every string the two
 * brands agree on straight into `src/locales/**`, then stopped. The rows below
 * are the residue — surfaces where live NM and live BG say *different things*,
 * so no single locale value can be right for both. Ours shipped one value and
 * therefore shipped at least one brand's wrong word:
 *
 *   • the PDP wishlist control announced "Add to Wishlist" on NM, where live
 *     says "Add to favorites" at all six widths;
 *   • the header bag announced "My cart, number of items: N" on both, a phrase
 *     that appears on NEITHER brand — live NM says "Shopping bag", live BG
 *     "Shopping Bag containing N items";
 *   • BG's Add-to-Bag button read "Add To Bag" against live BG's "Add to Bag";
 *   • BG's cart summary read NM's "Shipping" / "Estimated Total" against live
 *     BG's "Estimated Shipping" / "Total".
 *
 * WHAT IS *NOT* HERE, ON PURPOSE. Only strings that genuinely diverge between
 * the brands. A string both brands say stays in the ordinary locale path —
 * forking copy that has no reason to differ would double the translation
 * surface for nothing and make every key a guessing game ("is this one
 * branded?"). `summary.subtotal` is the worked example: live NM does not render
 * the row at all and live BG says "Subtotal", exactly ours, so it is absent
 * below.
 *
 * WHY BRAND-KEYED AND NOT A BRAND SUFFIX INSIDE THE LOCALE JSON: this codebase
 * already expresses brand differences as a typed keyed map read synchronously
 * through `useBrand()` — `header-composition.ts`, `overlay-model.ts`,
 * `mini-cart-behavior.ts`, `components/product-price/brand-variants.ts` and
 * `components/footer/content.ts`. This file follows `footer/content.ts` most
 * closely: same `{ key, defaultValue }` row shape, same `Record<Brand, …>`
 * store, same `…For(brand)` resolver. Translation stays in i18next; only the
 * *selection* of which key to translate moves into the brand layer.
 *
 * HOW A MISSING BRAND FAILS: {@link BRAND_COPY} is `Record<Brand, BrandCopy>`
 * and `BrandCopy` has no optional members, so adding a brand to
 * {@link Brand} — or omitting a row from one brand's document — is a
 * TYPE ERROR at the declaration, not a silent runtime fallback to NM.
 * Inheriting NM's value is precisely the failure that put NM's header, footer
 * and mobile structure on BG; the compiler is what stops it recurring.
 *
 * PROVENANCE: every row cites the saved production DOM dump under
 * `.reference/captures/` it was read from. Note each capture file contains the
 * page TWICE, so a citation names the first occurrence only.
 */

/** One brand's choice of i18n key for a single string. */
export interface BrandCopyEntry {
    /**
     * Namespace-qualified i18n key (`'cart:heading'`, `'product:addToCart'`).
     * Qualified rather than bare so a consumer can pass it to whichever `t` it
     * already holds regardless of that component's default namespace — the
     * idiom `bonus-product-modal/index.tsx` and `product-drawer/index.tsx`
     * already use.
     */
    key: string;
    /** Fallback copy, and the documentation of what the key is expected to say. */
    defaultValue: string;
}

/** Every string this storefront renders differently on NM than on BG. */
export interface BrandCopy {
    /**
     * The storefront's own name, as it appears in document metadata:
     * `<title>`'s suffix and `<meta property="og:site_name">`
     * (`components/seo-meta/index.tsx`).
     *
     * NOT a UI label — it is the only row here that never paints inside the
     * page. It belongs in this document anyway, and for this document's own
     * stated reason: live NM and live BG say different things, so no single
     * value can be right for both. It was a single brand-blind locale key
     * (`common:defaultSiteName` = "Storefront Next: Market Street") until
     * 2026-08-21, which put a name belonging to neither brand in the browser
     * tab and — because `og:site_name` is what Slack, Teams and iMessage render
     * on a link-preview card — in the first thing anyone sees before they
     * click. Every route on BOTH brands carried it; BG's home was the sole
     * title that read correctly, and even that leaked the stock name into its
     * social card.
     *
     * Resolved ONCE, inside `SeoMeta`, so every route inherits it. No route
     * passes it and none should: a per-route site name is how one page ends up
     * disagreeing with the other twenty.
     */
    siteName: BrandCopyEntry;
    /**
     * Accessible name of the "save this product" control (`icons/heart-icon.tsx`),
     * unfilled state.
     */
    wishlistAdd: BrandCopyEntry;
    /**
     * Accessible name of the header bag trigger (`header/cart-badge.tsx`).
     * Interpolates `{{count}}` on brands whose live label carries one.
     */
    cartBadge: BrandCopyEntry;
    /** The primary Add-to-Bag CTA, every surface that renders one. */
    addToBag: BrandCopyEntry;
    /** The cart page's H1 (`cart/cart-content.tsx`, `cart/cart-empty.tsx`). */
    cartHeading: BrandCopyEntry;
    /**
     * Cart-page order-summary row labels. Applied ONLY where
     * `components/order-summary/index.tsx` has its `cartPageAnatomy` prop —
     * i.e. on the cart page, the one surface these were measured on. Checkout
     * and account order details render the same component with no anatomy and
     * keep their prior labels, because no capture covers those surfaces.
     */
    cartSummaryTotal: BrandCopyEntry;
    cartSummaryTax: BrandCopyEntry;
    cartSummaryShipping: BrandCopyEntry;
    /**
     * Placeholder of the promo-code INPUT, on a surface whose promo entry has
     * actually been measured — i.e. wherever `PromoCodeFields` is handed a
     * `PromoFieldComposition`. Read in
     * `components/promo-code-form/promo-code-field.tsx` behind that same gate;
     * a surface with no measured composition keeps the template's shared
     * `cart:promoCode.placeholder` ("Promo code..."). Account order details is
     * not a surface for this row at all — it mounts `OrderSummary` without
     * `showPromoCodeForm` (`account/order-details/index.tsx:237`), so it
     * renders no promo entry to place a string in.
     *
     * WHAT LIVE ACTUALLY DRAWS, recorded because the element is NOT a
     * placeholder and the difference must not be quietly claimed as applied.
     * On BOTH brands live's `<input>` carries `placeholder=" "` — one space —
     * and the visible words are a SIBLING absolutely positioned over it at
     * `top-[50%] translate-y-[-50%]`: the resting half of a floating-label
     * control. NM's sibling is a `<label>`, BG's a grey `rgb(191,187,191)`
     * `<span>`; each brand's row below cites its own node. At rest the two
     * mechanisms paint the same words in the same place, which is what makes
     * the STRING transferable to a plain placeholder. The float-on-focus is a
     * structural difference this row does not reproduce and does not claim to.
     */
    promoPlaceholder: BrandCopyEntry;
    /**
     * Visible label of the CART LINE-ITEM save control
     * (`cart/cart-item-add-to-wishlist-button.tsx`), unsaved state only.
     *
     * Distinct from {@link wishlistAdd}, which is the PDP's heart icon: a
     * different component on a different surface, and live NM gives the two
     * different words ("Save For Later" in the bag, "Add to favorites" on the
     * PDP). Sharing one row would force one of those two to be wrong.
     */
    cartItemSave: BrandCopyEntry;
    /**
     * Accessible name of the same control. Separate row rather than a derived
     * suffix because live NM's accessible name is not its visible label —
     * it appends the product title so the two bag lines do not announce
     * identically. Interpolates `{{productName}}` on brands whose live label
     * carries one; the value is passed unconditionally and a brand whose
     * string has no placeholder simply ignores it, exactly as `cartBadge`
     * passes `{{count}}` to NM's count-free bag name.
     */
    cartItemSaveAria: BrandCopyEntry;
    /**
     * The cart page's primary checkout CTA — the button that leaves the bag.
     *
     * Applied ONLY where the cart-page anatomy is present, i.e. the one surface
     * these were read on. Checkout and account order details mount the same
     * summary without it and keep their prior label, because no capture covers
     * a checkout-page CTA.
     *
     * NM IS DELIBERATELY UNCHANGED HERE, and the reason matters more than the
     * value. Live NM's desktop cart carries no plain checkout button at all: it
     * renders a sign-in / guest gate ("Sign in to checkout faster" and "Guest
     * Checkout", `nm-cart-d-1280/App.tsx:447` and `:463`), which ours already
     * reproduces as `CartCheckoutGate`. So NM's row below is unreachable on the
     * cart today, and giving it a measured-looking value would be inventing one
     * for an element live does not draw. NM's MOBILE bar is a different control
     * on a different surface and reads `cart:checkout.mobileBarCta`, cited at
     * its own call site.
     */
    cartCheckoutCta: BrandCopyEntry;
}

/**
 * Neiman Marcus.
 *
 * Where a row names an existing key with its existing value, NM is UNCHANGED by
 * this document — the row exists so that BG can differ, not because NM moved.
 */
const NM_COPY: BrandCopy = {
    /*
       `<meta property="og:site_name" content="Neiman Marcus">` — read off live
       NM's own head in four independent raw dumps:
       `.reference/pdp-apparel-1.desktop.html:20`,
       `.reference/pdp-apparel-2-or-nonapparel.desktop.html:20`,
       `.reference/pdp-sale-apparel.desktop.html:20`,
       `.reference/mini-cart-open.desktop.html:20`.

       The raw dumps are the source and the captures cannot be: the decompiled
       `App.tsx` frames under `.reference/captures/` carry the BODY, so no
       `<head>` question is answerable there at all. The dumps do not double the
       way the captures do (one `<html>`, one `<body>` each), so these are four
       readings, not two.

       Live's `<title>` agrees at eight more places and settles that the site
       name is the SUFFIX, not a prefix: `Designer Clothing for Women | Neiman
       Marcus` (`.reference/plp-womens-clothing.desktop.html:53`), `Mac Duggal
       … | Neiman Marcus` (`.reference/pdp-apparel-1.desktop.html:1`), `Dress at
       Neiman Marcus` (`.reference/search-dress.desktop.html:53`).

       WHAT IS NOT TAKEN, AND IT IS DELIBERATE. Live NM's SEPARATOR is not one
       character: `|` on PLP/PDP, the bare word `at` on search, and lead-position
       on home (`Neiman Marcus | Designer Clothing, Shoes, Handbags, & Beauty`,
       `.reference/homepage.desktop.html:33`) and cart (`Neiman Marcus | Cart`,
       `.reference/cart.desktop.html:1`). Live BG is equally inconsistent with
       itself — `|` on PLP, ` - ` on PDP and cart. A per-page-type title formula
       is a different change on a different seam (`SeoMeta`'s composition, not
       this document's vocabulary), so `SeoMeta`'s single ` | ` suffix stands and
       only the NAME moves here. Recorded so the next reader does not have to
       re-derive that the separator was seen and left.
    */
    siteName: { key: 'common:siteName', defaultValue: 'Neiman Marcus' },
    /*
       `<button aria-label="Add to favorites">` — nm-pdp-d-1280/App.tsx:452,
       d-1440:451, d-1024:448, d-768:447, m-375:140, m-440:140. All six widths,
       lower-case "favorites". NM's vocabulary is "favorites" throughout: the
       header links `myfavorites/myFavoriteItems.jsp` behind `alt="my favorite
       icon"` (:116-117) and the footer column reads "My Favorites".

       New key rather than re-pointing `addToWishlist`, because `addToWishlist`
       is still BG's row and is already translated into thirteen locales.
    */
    wishlistAdd: { key: 'product:addToFavorites', defaultValue: 'Add to favorites' },
    /*
       `<span aria-label="Shopping bag" role="button">` — home-d-1280:133,
       home-m-375:62, nm-pdp-d-1280:121, nm-pdp-m-375:58, nm-cart-d-1280:55.
       Sentence case, and NO item count: NM's accessible name is the same
       whether the bag is empty or full, so this row takes no `{{count}}`.

       (Live nests this span inside a `<div aria-label="view cart">` wrapper —
       home-d-1280:132. Ours is a single button, and the span is the element
       that actually carries the glyph, so its name is the one reproduced.)
    */
    cartBadge: { key: 'cart:badge.ariaLabel', defaultValue: 'Shopping bag' },
    /*
       `<button …>Add To Bag</button>` — capital T — nm-pdp-d-1280:450,
       d-1440:449, d-1024:446, d-768:445, m-375:215, m-440:215, and again in
       nm-minicart-m-375:255 / m-440:255. Eight captures.

       PER-SURFACE ADJUDICATION. NM is not internally consistent: its product
       drawer prints `Add to Bag` in sentence case (nm-product-drawer-d-1440:1143
       — one capture, one width). NM takes the six-width value on every surface
       here, for three reasons. (1) Weight of evidence: eight captures against
       one. (2) It is indistinguishable on screen — BOTH NM literals sit under
       `uppercase` (`:450` and `:1143` alike), so both paint ADD TO BAG, and a
       screen reader speaks the two identically; the split is invisible to every
       user. (3) Our drawer and our PDP render the SAME component
       (`product-drawer/index.tsx:302` mounts `<ProductCartActions>`), so
       honouring the split would mean threading a surface prop through the CTA
       for a difference nobody can perceive. Recorded rather than silently
       dropped: if the drawer's sentence case is later shown to be deliberate,
       the row to add is a per-surface arm here, not a second locale key.
    */
    addToBag: { key: 'product:addToCart', defaultValue: 'Add To Bag' },
    /* `<h1 …>Your Shopping Bag</h1>` — nm-cart-d-1280:192. Ours already matches;
       the row exists so BG's shorter heading has somewhere to live. */
    cartHeading: { key: 'cart:heading', defaultValue: 'Your Shopping Bag' },
    /*
       `Order Total` — nm-cart-d-1280:422, and identically at d-1440:422,
       d-1024:422, d-768:422, m-375:309, m-440:309. Ours renders "Estimated
       Total" here, so this row is a real change on NM.

       NOT the mobile fixed bar: nm-cart-m-375:352 reads "Estimated Total" in
       the sticky bar, which `order-summary/mobile-heading.tsx` renders and ours
       already matches. That component is deliberately untouched.
    */
    cartSummaryTotal: { key: 'cart:summary.orderTotal', defaultValue: 'Order Total' },
    /*
       UNESTABLISHED, and inert. Live NM's cart summary renders no Tax row at
       all (`CART_SUMMARY_ANATOMY.nm.showTaxRow = false`, from `grep -ac 'Tax'`
       returning 0 over the summary block at all six widths), so there is no
       live label to copy and this entry is never rendered on the cart page. It
       holds today's shared value so the record stays total; it is NOT evidence
       about NM.
    */
    cartSummaryTax: { key: 'cart:summary.tax', defaultValue: 'Tax' },
    /* `Shipping` — nm-cart-d-1280:421 (…:421 desktop, m-375:307 mobile). Ours
       already matches; the row exists so BG's "Estimated Shipping" can differ. */
    cartSummaryShipping: { key: 'cart:summary.shipping', defaultValue: 'Shipping' },
    /*
       `<label className="block pointer-events-none absolute left-0 top-[50%]
       …">Enter Promo Code</label>`, floating over `<input type="text"
       placeholder=" ">` inside the summary's 50px bordered promo row, with an
       underlined `Apply` inset at its right.

       MEASURED at SIX WIDTHS: `.reference/captures/nm-cart-{d-1280,d-1440,
       d-1024,d-768,m-375,m-440}/App.tsx`. `/usr/bin/grep -aoi 'Enter Promo
       Code'` returns 2 in every one of the six — the corpus emits each page
       twice, so that is ONE occurrence per width, six widths and not twelve.
       d-1280:427 is the first. Positive control `div` on the same command
       returns 804-1016 per file, so the companion zero for BG's string in
       these same files is real absence and not a failed read.

       CORROBORATED ON A SECOND SURFACE AND A SECOND EVIDENCE TIER. Live NM's
       CHECKOUT paints the same string in the same control, in the owner's own
       browser screenshots: `.reference/live-shots/
       owner-live-nm-checkout-1280-fullpage.png` (right rail, white bordered
       box on the grey rail, `Apply` underlined at the right) and
       `owner-live-nm-checkout-390-desktopUA.png` (the same box directly above
       `Order Summary`). Decompiled frames and live pixels agree, which is why
       this row is correct on NM's checkout rail as well as on its cart — and
       the rail is a measured surface (`checkout-composition.ts` gives NM a
       non-null `railPromoField`), so the gate lets it through there.
    */
    promoPlaceholder: { key: 'cart:promoCode.enterPromoCode', defaultValue: 'Enter Promo Code' },
    /*
       `<button aria-label="Save For Later - Autumn Cashmere Cashmere Crewneck
       Cable-Knit Sweater" …>Save For Later</button>` — nm-cart-d-1280:220 and
       :328 (the two bag lines), and byte-identical at d-1440, d-1024, d-768,
       m-375, m-440. `/usr/bin/grep -aic 'save for later'` returns 4 in every
       one of the six NM cart captures — 2 line items x the two copies of the
       page each capture file holds — so this is six widths, not one.

       Ours said "Add to Wishlist" in BOTH halves. The word "wishlist" is
       absent from NM's cart entirely: the same grep for 'wishlist' returns 0
       at all six widths, against a positive control ('shopping bag') that
       returns 6. The four 'favorite' hits in those captures are the header's
       `myfavorites/myFavoriteItems.jsp` link (:50-51), not this control.

       SCOPED TO THE UNSAVED STATE, DELIBERATELY. Ours also renders
       `removeFromWishlist`, `removingFromWishlist` and `addingToWishlist`
       (same component). Every NM cart capture shows this control in its
       UNSAVED state only, so there is no live reading for the other three and
       they are left alone rather than guessed into NM's vocabulary. That is a
       recorded scoping decision, not an oversight — the saved-state wording is
       tracked separately.
    */
    cartItemSave: { key: 'cart:lineItem.saveForLater', defaultValue: 'Save For Later' },
    /* Same two lines: the accessible name is the visible label plus ` - ` and
       the product title, which is what stops two bag rows announcing as the
       same control. */
    cartItemSaveAria: {
        key: 'cart:lineItem.saveForLaterAria',
        defaultValue: 'Save For Later - {{productName}}',
    },
    /* Unreachable on the cart, and named rather than omitted so the compiler
       keeps this document exhaustive. See the interface note: live NM's desktop
       rail is the sign-in / guest gate, not a checkout button, and the mobile
       bar's own word is measured separately. This restates the existing shared
       value, so an NM surface that ever falls through to it is unmoved. */
    cartCheckoutCta: { key: 'cart:checkout.continueToCheckout', defaultValue: 'Continue to Checkout' },
};

/** Bergdorf Goodman. */
const BG_COPY: BrandCopy = {
    /*
       `<meta property="og:site_name" content="Bergdorf Goodman">` — live BG's
       own head, three independent raw dumps:
       `.reference/bg-pdp-regular.desktop.html:22`,
       `.reference/bg-pdp-sale.desktop.html:22`,
       `.reference/bg-mini-cart-open.desktop.html:22`.

       Live's `<title>` agrees at seven more: `Bergdorf Goodman | The Iconic
       Luxury Department Store` (`.reference/bg-homepage.desktop.html:22`, and
       byte-identical at `.reference/bg-homepage.mobile.html:22`), `Women's
       Designer Dresses | Bergdorf Goodman`
       (`.reference/bg-plp-womens-dresses.desktop.html:22`), `Dress at Bergdorf
       Goodman` (`.reference/bg-search-dress.desktop.html:22`), `Shopping Bag -
       Bergdorf Goodman` (`.reference/bg-cart.desktop.html:14`).

       No abbreviation. Live BG never writes "BG" or "Bergdorf" alone in either
       tag at any of those ten readings — the wordmark art is the only place the
       short form appears, and art is not copy.
    */
    siteName: { key: 'common:siteNameBg', defaultValue: 'Bergdorf Goodman' },
    /*
       UNESTABLISHED — BG is deliberately LEFT AT TODAY'S VALUE, not given NM's.

       Live BG's PDP save control is an unlabelled `<span>` wrapping
       `<img alt="Wishlist Cta">` (bg-pdp-d-1280:282, d-1440:282, d-768:154,
       m-375:144 — 14 occurrences across the BG captures, always that exact
       alt). Its computed accessible name is therefore the literal string
       "Wishlist Cta", which is an authoring artefact ("Cta" = call-to-action),
       not shopper-facing copy — reproducing it would import a real screen-reader
       defect. No BG capture carries an `aria-label` containing "wishlist" or
       "favorite" anywhere.

       So: BG's NOUN is established — "Wishlist", from the header link
       `Wishlist` → `/p/wishlist` (bg-pdp-d-1280:102) — but BG's control LABEL
       is not. BG keeps the value it ships today, which already uses that noun.
       What is ruled OUT is NM's "favorites", which is NM's word alone.
    */
    wishlistAdd: { key: 'product:addToWishlist', defaultValue: 'Add to Wishlist' },
    /*
       `<img alt="Shopping Bag containing 0 items">` — bg-home-m-375:105,
       m-440:105, bg-home-d-1024:105, bg-drawer-375:105, bg-drawer-clothing-375:105,
       bg-search-color-375:66 — and the SAME pattern at a non-zero count,
       `alt="Shopping Bag containing 2 items"`, at bg-pdp-d-1024:108, d-768:108,
       m-375:108, m-440:108. Two different counts in ten captures is what makes
       `{{count}}` a measured fact rather than an assumption.

       No plural suffix. Live's own string is "0 items" and "2 items"; a live
       reading at count 1 does not exist, so `_one` / `_other` forms would be
       invented. A flat `{{count}} items` reproduces both evidenced values
       exactly.

       KNOWN DIVERGENCE, FLAGGED NOT RATIONALIZED: at 1280+ live BG's bag is a
       text link reading `Bag` with no aria-label (bg-home-d-1280:107,
       bg-search-d-1280:108, bg-cart-d-1280:38), so live announces "Bag" there
       and "Shopping Bag containing N items" at 1024 and below. Ours renders the
       glyph at EVERY width (the desktop word "Bag" is added beside it, via
       `cart:badge.label`), and an aria-label is one attribute — so the icon
       form's name is the one taken. Ours therefore announces the longer,
       count-bearing name at 1280 where live announces "Bag".
    */
    cartBadge: { key: 'cart:badge.ariaLabelBg', defaultValue: 'Shopping Bag containing {{count}} items' },
    /*
       `<button aria-label="Add to Bag"><label>Add to Bag</label>` — sentence
       case, in BOTH the label and the aria-label — bg-pdp-d-1280:383-384,
       d-1440:383-384, d-1024:254-255, d-768:255-256, m-375:239-240,
       m-440:239-240. All six widths.

       BG's button lost `text-transform: uppercase` when the per-brand transform
       landed (`--pdp-atb-text-transform`), which exposed the shared literal's
       capital T. BG needs the string fixed, not the transform put back.
    */
    addToBag: { key: 'product:addToCartBg', defaultValue: 'Add to Bag' },
    /*
       `<h2 …>Shopping Bag  <span>( 2   items )</span></h2>` —
       bg-cart-d-1280:49, d-1440:49 (equivalent), d-1024:42, m-375:42. No
       "Your", and `uppercase` in CSS.

       ONLY the heading text is taken. The grey `( N items )` suffix is a second
       styled element inside the H1, not copy — it is markup and colour
       (`rgb(149,152,154)`, `font-normal`, 16px) and belongs to a cart-layout
       change, not to this seam. Recorded and left.
    */
    cartHeading: { key: 'cart:headingBg', defaultValue: 'Shopping Bag' },
    /* `<p>Total</p>` — bg-cart-d-1280:252, d-1440:252, d-1024:245, d-768:245,
       m-375:248, m-440:248. All six widths. Bare "Total", not "Order Total". */
    cartSummaryTotal: { key: 'cart:summary.total', defaultValue: 'Total' },
    /* `<p>Estimated Tax</p>` — bg-cart-d-1280:244, d-1440:244, d-1024:237,
       d-768:237, m-375:240, m-440:240. All six widths. */
    cartSummaryTax: { key: 'cart:summary.estimatedTax', defaultValue: 'Estimated Tax' },
    /* `<p>Estimated Shipping</p>` — bg-cart-d-1280:248, d-1440:248,
       d-1024:241, d-768:241, m-375:244, m-440:244. All six widths. */
    cartSummaryShipping: { key: 'cart:summary.estimatedShipping', defaultValue: 'Estimated Shipping' },
    /*
       `<span className="block absolute left-4 top-[50%] text-[rgb(191,_187,_191)]
       …">Apply Promo Code</span>`, over `<input type="text" placeholder=" ">`
       inside a `<label>` wrapper, with a black `Apply` button beside it.

       MEASURED at SIX WIDTHS: `.reference/captures/bg-cart-{d-1280,d-1440,
       d-1024,d-768,m-375,m-440}/App.tsx`, `/usr/bin/grep -aoi 'Apply Promo
       Code'` returning 2 in each — one occurrence per width after halving for
       the corpus's double emission. d-1280:261 is the first; the input it
       labels is :260 and the `Apply` button :262. Positive control `div`
       returns 648-834 per file. The other `promo` hits in those files are a
       red `Promo applied : 25 % off - $ 430` line (:133), a `Promo applied`
       row (:240) and a `View promotional image` button (:312) — different
       elements, named here so a future count is not read as this control.

       SCOPED TO A MEASURED SURFACE, AND THE SCOPE IS LOAD-BEARING. Live BG's
       CHECKOUT does not draw this control in any captured state: the owner's
       `owner-live-bg-checkout-1280-fullpage.png` and
       `owner-live-bg-checkout-390-desktopUA.png` both paint a `PROMO AND GIFT
       CODES` heading over a COLLAPSED `Promo Code` chevron row in the main
       column — no input visible — and BG's order rail carries no promo entry
       at all. BG's checkout placeholder is therefore UNMEASURED and must keep
       the template's shared string. That is what the `promoField` gate in
       `promo-code-field.tsx` delivers without a second decision here: BG's
       checkout arm passes no composition (`checkout-composition.ts` sets
       `promoPlacement: 'mainColumn'` and `railPromoField: null`), so this row
       cannot reach it.
    */
    promoPlaceholder: { key: 'cart:promoCode.applyPromoCode', defaultValue: 'Apply Promo Code' },
    /*
       UNESTABLISHED — BG is deliberately LEFT AT TODAY'S VALUE, and in
       particular is NOT given NM's "Save For Later".

       Live BG's cart renders NO save control on a line item at any width.
       `/usr/bin/grep -aic 'save for later'` returns 0 in all six BG cart
       captures, and 'wishlist' returns 2 at d-1280/d-1440 and 0 at the other
       four — both hits being the HEADER link to `/e/login`
       (bg-cart-d-1280:35), not a line-item control. Positive control on the
       same command ('subtotal') returns 2 everywhere, so the zeroes are real
       absence and not a failed read.

       BG's authored copy DOES carry a string for this control:
       `SHOPPING_CART_MOVE_TO_WISHLIST_CTA` = "Move to Wishlist", in the
       Contentful rich-text payload embedded in
       `.reference/bg-cart.desktop.html`. It is recorded here and NOT applied,
       because a dictionary entry is evidence that BG AUTHORED a string, not
       evidence of what BG PAINTS — and we have positive proof at six widths
       that BG paints no such control at all. Landing an unrendered CMS value
       as shopper-facing copy would be the harvest-without-a-picture failure in
       its purest form: there is no capture that could veto it.

       This is the same adjudication `wishlistAdd` above already got, and for
       the same reason: BG's NOUN is established ("Wishlist", from the header
       link) and BG's control LABEL is not, so BG keeps the value it ships
       today, which already uses that noun. What is ruled OUT is NM's phrase.
    */
    cartItemSave: { key: 'product:addToWishlist', defaultValue: 'Add to Wishlist' },
    /* Same row, same reasoning. BG's accessible name takes no `{{productName}}`
       because there is no live BG control to have measured one from; the
       component passes the value regardless and this string ignores it. */
    cartItemSaveAria: { key: 'product:addToWishlist', defaultValue: 'Add to Wishlist' },
    /*
       `Proceed to checkout` — live BG's cart CTA, and it is the same words at
       every width BG was captured at. `/usr/bin/grep -aoc 'Proceed to checkout'`
       over the six `bg-cart-` capture directories' `App.tsx` returns 4 at
       m-375 and m-440
       and 2 at d-768, d-1024, d-1280 and d-1440, against `div` controls of
       648-834. Each capture holds the page TWICE, so that is two CTAs per
       mobile page and one per desktop page — six widths, not one reading.
       The mobile node is `bg-cart-m-375/App.tsx:45`, an `<a>` to
       `/checkout` carrying `uppercase w-full h-14 leading-[56px]
       tracking-[0.4px] min-w-[261px]`, which is the geometry our BG mobile CTA
       already reproduces.

       Ours said `Continue to Checkout`, which appears NOWHERE in BG's cart:
       the same grep for 'continue to checkout' returns 0 at all six widths
       against those same controls.

       LOWERCASE `checkout` IS LIVE'S, and it is deliberate even though the
       element is `uppercase` and therefore paints identically either way. The
       casing survives into the accessible name and into anything reading text
       content, so matching live costs nothing and diverging would be a
       difference we could not justify if anyone looked.
    */
    cartCheckoutCta: { key: 'cart:checkout.proceedToCheckout', defaultValue: 'Proceed to checkout' },
};

/**
 * Each brand's copy document.
 *
 * `Record<Brand, BrandCopy>` with no optional members: a brand with no entry,
 * or a document missing a row, does not compile.
 *
 * PROVENANCE. This map is a RE-EXPORT — neither arm carries a value of its own,
 * and every string's citation sits on its own row inside the document it points
 * at:
 *   nm -> NM_COPY, copy.ts:133
 *   bg -> BG_COPY, copy.ts:234
 * BG's document is the one that had to be adjudicated row by row (its
 * `cartItemSave` block above cites six BG cart captures plus
 * `.reference/bg-cart.desktop.html`, and records what it declines to apply);
 * NM's is the template's pre-existing wording where no live reading changed it.
 * Nothing was re-derived at this line — the rows are where a reading is
 * checkable, and this note only says where to look.
 */
export const BRAND_COPY: Record<Brand, BrandCopy> = {
    nm: NM_COPY,
    bg: BG_COPY,
};

/**
 * Resolve a brand's copy document, falling back to {@link DEFAULT_BRAND} for
 * renders with no site context (isolated stories, early error boundaries) —
 * the same fallback `useBrand()` itself applies, and the same resolver shape as
 * `footerContentFor`.
 */
export function brandCopyFor(brand: Brand): BrandCopy {
    return BRAND_COPY[brand] ?? BRAND_COPY[DEFAULT_BRAND];
}
